/**
 * ValidationBanner
 * ----------------
 * Sticky top banner that lists invoice validation issues. Each row is a
 * button that scrolls to + focuses the corresponding form field via
 * `[data-invalid-field="<id>"]` selector. Compact and collapsible.
 *
 * Designed to coexist with AppHeader (which has `position: sticky` too).
 * We use `top-16` to sit just below the header on desktop and mobile.
 */
import { useState } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { ValidationIssue } from "@/lib/invoiceValidation";

interface Props {
  issues: ValidationIssue[];
  /** Optional: hide entirely (used to suppress before user attempted submit). */
  visible?: boolean;
  /** Called when user clicks the X — caller decides whether to truly hide. */
  onDismiss?: () => void;
}

const focusInvalidField = (fieldId: string) => {
  // Prefer exact data-invalid-field match; fall back to data-field-group
  const target =
    document.querySelector<HTMLElement>(`[data-invalid-field="${CSS.escape(fieldId)}"]`) ??
    document.querySelector<HTMLElement>(`[data-field-group="${CSS.escape(fieldId)}"]`);
  if (!target) return;
  target.scrollIntoView({ behavior: "smooth", block: "center" });
  // Try focusing the underlying input/select if target is a wrapper
  const focusable =
    target.matches("input, select, textarea, button")
      ? target
      : target.querySelector<HTMLElement>("input, select, textarea, button");
  if (focusable) {
    setTimeout(() => focusable.focus({ preventScroll: true }), 250);
  }
};

const ValidationBanner = ({ issues, visible = true, onDismiss }: Props) => {
  const [expanded, setExpanded] = useState(true);
  const errors = issues.filter((i) => i.severity === "error");
  const warnings = issues.filter((i) => i.severity === "warning");

  if (!visible || issues.length === 0) return null;

  const hasErrors = errors.length > 0;
  const summary = hasErrors
    ? `${errors.length} ${errors.length === 1 ? "eroare blocantă" : "erori blocante"}${
        warnings.length > 0 ? ` + ${warnings.length} avertisment${warnings.length === 1 ? "" : "e"}` : ""
      }`
    : `${warnings.length} avertisment${warnings.length === 1 ? "" : "e"}`;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        "sticky top-16 z-30 mx-auto max-w-xl",
        "rounded-lg border shadow-md backdrop-blur-sm",
        hasErrors
          ? "border-destructive/40 bg-destructive/10"
          : "border-warning/40 bg-warning/10",
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <AlertTriangle
          className={cn(
            "h-4 w-4 shrink-0",
            hasErrors ? "text-destructive" : "text-warning",
          )}
        />
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex-1 text-left text-sm font-medium text-foreground hover:text-foreground/80"
          aria-expanded={expanded}
          aria-controls="validation-banner-list"
        >
          {summary} — {expanded ? "ascunde" : "vezi detalii"}
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Restrânge" : "Extinde"}
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </Button>
        {onDismiss && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={onDismiss}
            aria-label="Închide bannerul"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {expanded && (
        <ol
          id="validation-banner-list"
          className="border-t border-border/50 px-3 py-2 space-y-1 text-sm max-h-64 overflow-y-auto"
        >
          {[...errors, ...warnings].map((issue, idx) => (
            <li key={`${issue.field}-${issue.code}-${idx}`}>
              <button
                type="button"
                onClick={() => focusInvalidField(issue.field)}
                className={cn(
                  "w-full text-left rounded px-2 py-1 hover:bg-background/60 transition-colors",
                  "flex items-start gap-2",
                )}
              >
                <span
                  className={cn(
                    "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-semibold",
                    issue.severity === "error"
                      ? "bg-destructive text-destructive-foreground"
                      : "bg-warning text-warning-foreground",
                  )}
                >
                  {idx + 1}
                </span>
                <span className="flex-1">
                  <span className="font-medium text-foreground">{issue.fieldLabel}:</span>{" "}
                  <span className="text-muted-foreground">{issue.message}</span>
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

export default ValidationBanner;
